import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBillableItem, useDeleteBillableItem, useProjectById } from '@/hooks';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { BillableItemForm } from '@/components/BillableItemForm';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { BillableItemReceiptUpload } from '@/components/BillableItemReceiptUpload';

export default function BillableItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: itemData, isLoading: itemLoading, error: itemError } = useBillableItem(id);
  const deleteBillableItem = useDeleteBillableItem();

  const item = itemData?.billable_item;

  // Phase 7.5.3: Load project to get workflow_stage for fee type restrictions
  const { data: projectData, isLoading: projectLoading } = useProjectById(
    item?.project_id
  );
  const project = projectData?.project;

  const isLoading = itemLoading || (item && projectLoading);
  const error = itemError;

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteBillableItem.mutateAsync(id);
      window.scrollTo(0, 0);
      navigate(`/projects/${item?.project_id}`);
    } catch (error) {
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Billable Item">
        <div className="space-y-6">
          <Skeleton className="h-12 w-full max-w-2xl" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout title="Billable Item">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load billable item: {(error as Error)?.message || 'Item not found'}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Edit Billable Item"
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/projects/${item.project_id}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Project
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </>
      }
    >
      <div className="max-w-3xl">
        <BillableItemForm
          mode="edit"
          item={item}
          projectId={item.project_id}
          projectNumber={item.project_number ?? null}  // Phase 9.6a: Use null for proposals/templates
          workflowStage={project?.workflow_stage}
          isTemplate={project?.is_template}
        />

        {/* Phase 10b.2: Receipt uploads — shown for billable_expense items only */}
        {item.category === 'billable_expense' && id && (
          <BillableItemReceiptUpload billableItemId={id} />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Billable Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this billable item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteBillableItem.isPending}
            >
              {deleteBillableItem.isPending ? 'Deleting...' : 'Delete Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
